import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Heading from "@/components/atoms/Heading";
import { INVITABLE_ROLE_TYPES } from "@/utils/roles";
import UserTable from "@/components/organisms/SettingsTables/UserTable";
import { useGetCompanyTeam } from "@/services/Company";
import Modal, { ModalCTAs } from "../../molecules/Modal";
import CTA from "@/components/atoms/CTA";
import Text from "@/components/atoms/Text";
import Group from "@/components/atoms/Group";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { InviteUserFormInput, InviteUserValidationSchema } from "./validation/InviteUserValidationSchema";
import { Container, Grid } from "@mantine/core";
import TextInput from "@/components/atoms/TextInput";
import Select from "@/components/atoms/Select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createUser } from "@/services/Settings";
import { notifications } from "@mantine/notifications";
import { SuccessNotificationObj, ErrorNotificationObj } from "@/utils/NotificationUtil";
import Stack from "@/components/atoms/Stack";
import { useAuth } from "@/contexts/AuthContext";

export default function TeamSettings() {
  const [openInviteModal, setOpenInviteModal] = useState<boolean>(false);

  const { t } = useTranslation();

  const { currentCompany, isAdminUser } = useAuth();
  const { data: activeTeamMembers } = useGetCompanyTeam(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });

  const isAdmin = isAdminUser(currentCompany);

  return (
    <Stack data-testid="users-settings">
      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:navigation.users")}
      </Heading>
      <UserTable
        activeTeamMembers={activeTeamMembers?.activeUsers || []}
        currentCompany={currentCompany}
        isAdmin={isAdmin}
        onClick={() => setOpenInviteModal(true)}
      />
      <InviteModal
        open={openInviteModal}
        onClose={() => setOpenInviteModal(false)}
        companyId={currentCompany?.companyId}
      />
    </Stack>
  );
}

interface InviteModalProps {
  open: boolean;
  onClose: () => void;
  companyId?: number;
}

const InviteModal: React.FC<InviteModalProps> = ({ open, onClose, companyId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
  } = useForm<InviteUserFormInput>({
    resolver: zodResolver(InviteUserValidationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: {
      firstname: "",
      lastname: "",
      email: "",
      role: "USER",
    },
  });

  const { mutate: mutateInviteUser, isPending: isPendingMutateInviteUser } = useMutation({
    mutationFn: async (data: InviteUserFormInput) => {
      const request = {
        companyId: companyId as number,
        ...data,
      };
      await createUser(request);
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:settings.userAdded"),
          "user-invited-success"
        )
      );
      queryClient.invalidateQueries({
        queryKey: ["getCompanyTeam", companyId],
      });
      reset();
      onClose();
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          t("notifications:settings.userAddedError"),
          "user-invited-error"
        )
      );
    },
  });

  const onSubmit = (data: InviteUserFormInput) => {
    mutateInviteUser(data);
  };

  return (
    <Modal opened={open} onClose={onClose} size="xl" title={<ModalHeader title={t("settings:inviteNewUser")} />}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container my={12}>
          <Grid>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="firstname"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      id="firstname"
                      required={true}
                      label={t("common:firstname")}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="lastname"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      id="lastname"
                      required={true}
                      label={t("common:lastname")}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      id="email"
                      required={true}
                      label={t("common:email")}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>
            {/*
              Commented out backend is not opened for phone yet even if DTO has it implemented
              <Grid.Col span={6}>
                <Controller
                control={control}
                name="phone"
                render={({ field, fieldState }) => {
                  return (
                    <PhoneField
                      {...field}
                      id="phone"
                      placeholder={t("common:phone")}
                      errormessage={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              </Grid.Col>
              />*/}
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="role"
                render={({ field, fieldState }) => {
                  return (
                    <Select
                      {...field}
                      id="role"
                      label={t("common:role")}
                      required={true}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      data={INVITABLE_ROLE_TYPES.map((role) => ({
                        value: role,
                        label: t(`enumerables:userRoles.${role}`),
                      }))}
                    />
                  );
                }}
              />
            </Grid.Col>
          </Grid>
        </Container>
        <ModalCTAs>
          <Group justify="flex-end">
            <CTA intent="secondary" onClick={onClose}>
              {t("actions:cancel")}
            </CTA>
            <CTA
              intent="primary"
              type="submit"
              data-testid="invite-submit-btn"
              disabled={!isValid || isPendingMutateInviteUser}
              loading={isPendingMutateInviteUser}
            >
              {t("settings:invite")}
            </CTA>
          </Group>
        </ModalCTAs>
      </form>
    </Modal>
  );
};

const ModalHeader = ({ title }: { title: string }) => {
  return (
    <Group>
      <SVGAsset name="user-white" />
      <Text size="xl">{title.toUpperCase()}</Text>
    </Group>
  );
};
